/**
 * The words and numbers around an announcement: when, how long until, and - for
 * Administration - whether it is showing right now. Pure, so they are tested
 * rather than eyeballed. Every time here is UTC, as everywhere else in the app.
 */

import type { Announcement, AnnouncementInput, AnnouncementTone } from '../../api/types.ts';

export const TONE_LABEL: Record<AnnouncementTone, string> = {
  info: 'Announcement',
  maintenance: 'Maintenance',
  critical: 'Service disruption',
};

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const text = /(?:Z|[+-]\d\d:\d\d)$/i.test(iso) ? iso : `${iso}Z`;
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function utcDay(iso: string | null | undefined): string {
  const d = parse(iso);
  return d
    ? d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })
    : '';
}

export function utcTime(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }) : '';
}

/** "Sat 19 Sept 22:00-01:00 UTC", or with both days when it spans more than one. */
export function eventWindow(a: Pick<AnnouncementInput, 'event_starts_at' | 'event_ends_at'>): string {
  const start = parse(a.event_starts_at);
  if (!start) return '';
  const end = parse(a.event_ends_at);
  if (!end) return `${utcDay(a.event_starts_at)} ${utcTime(a.event_starts_at)} UTC`;
  const hours = (end.getTime() - start.getTime()) / 3.6e6;
  // Overnight windows under a day read as one evening, the way people say them.
  if (hours < 24) {
    return `${utcDay(a.event_starts_at)} ${utcTime(a.event_starts_at)}-${utcTime(a.event_ends_at)} UTC`;
  }
  return `${utcDay(a.event_starts_at)} ${utcTime(a.event_starts_at)} - ${utcDay(a.event_ends_at)} ${utcTime(a.event_ends_at)} UTC`;
}

function span(ms: number): string {
  const h = Math.floor(ms / 3.6e6);
  const m = Math.floor((ms % 3.6e6) / 6e4);
  const s = Math.floor((ms % 6e4) / 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  if (h >= 24) return `${Math.floor(h / 24)} d ${pad(h % 24)} h ${pad(m)} m`;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/** "02 d 04 h 10 m", "03:12:09", "under way", or "over". */
export function countdown(
  a: Pick<AnnouncementInput, 'event_starts_at' | 'event_ends_at'>,
  now = Date.now(),
): string {
  const start = parse(a.event_starts_at);
  if (!start) return '';
  const end = parse(a.event_ends_at);
  const left = start.getTime() - now;
  if (left <= 0) return end && end.getTime() <= now ? 'over' : 'under way';
  return span(left);
}

export interface EventClock {
  /** Before the start, while it runs with a known end, or neither. */
  phase: 'starts' | 'remaining' | 'under way' | 'over';
  /** The counter for `starts` and `remaining`; empty otherwise. */
  left: string;
}

/**
 * The schedule card's clock: time to the start, then time to the end once it
 * has started. A window with no end has nothing to count down to while it runs.
 */
export function eventClock(
  a: Pick<AnnouncementInput, 'event_starts_at' | 'event_ends_at'>,
  now = Date.now(),
): EventClock | null {
  const start = parse(a.event_starts_at);
  if (!start) return null;
  if (start.getTime() > now) return { phase: 'starts', left: span(start.getTime() - now) };
  const end = parse(a.event_ends_at);
  if (!end) return { phase: 'under way', left: '' };
  if (end.getTime() <= now) return { phase: 'over', left: '' };
  return { phase: 'remaining', left: span(end.getTime() - now) };
}

export type AnnouncementStatus = 'live' | 'scheduled' | 'ended' | 'off';

/** Whether it is on people's screens right now, as Administration shows it. */
export function announcementStatus(a: Announcement, now = Date.now()): AnnouncementStatus {
  if (!a.is_active) return 'off';
  const from = parse(a.show_from);
  const until = parse(a.show_until);
  if (from && from.getTime() > now) return 'scheduled';
  if (until && until.getTime() <= now) return 'ended';
  return 'live';
}

export const STATUS_LABEL: Record<AnnouncementStatus, string> = {
  live: 'Showing',
  scheduled: 'Scheduled',
  ended: 'Ended',
  off: 'Off',
};

/** ISO from the server → the value a `datetime-local` input shows, in UTC. */
export function toUtcInput(iso: string | null | undefined): string {
  const d = parse(iso);
  return d ? d.toISOString().slice(0, 16) : '';
}

/** A `datetime-local` value → what the API takes (no zone = UTC), or null. */
export function fromUtcInput(value: string): string | null {
  return value ? `${value.length === 16 ? `${value}:00` : value}` : null;
}
